import { createConsumer, Consumer, Subscription } from "@rails/actioncable";
import { useEffect, useRef } from "react";

/**
 * ---- Internal caches ----
 */
const consumerCache = new Map<string, Consumer>();
const subscriptionCache = new Map<string, Subscription>();
// Handlers of every mounted hook instance sharing a subscription; its size is the refcount.
type Handlers = {
  received: (data: any) => void;
  connected: () => void;
  disconnected: () => void;
};
const handlerCache = new Map<string, Set<Handlers>>();

type CableUrl = string | (() => string);

/**
 * Create or reuse a Consumer, keyed by a caller-stable string.
 * `url` may be a function so each (re)connect re-evaluates the latest ticket.
 */
function getConsumer(key: string, url: CableUrl, protocols?: string[]): Consumer {
  if (!consumerCache.has(key)) {
    consumerCache.set(key, createConsumer(url as unknown as string, protocols));
  }
  return consumerCache.get(key)!;
}

/** True when the consumer for this key has an open or connecting socket. */
export function isConsumerActive(key: string): boolean {
  return consumerCache.get(key)?.connection.isActive() ?? false;
}

/**
 * Reopen the socket for this key if it has dropped. A no-op while it is open or
 * connecting, or before any subscription has created the consumer.
 */
export function reconnectConsumer(key: string) {
  consumerCache.get(key)?.connect();
}

function subscriptionKey(channel: string, params: Record<string, unknown>) {
  return `${channel}:${JSON.stringify(params)}`;
}

/**
 * Public hook
 */
export function useActionCable<TPayload>(options: {
  url: CableUrl;
  channel: string;
  params?: Record<string, unknown>;
  protocols?: string[];
  /** Subscriptions passing the same key share one websocket. */
  consumerKey?: string;
  enabled?: boolean;
  onReceived: (data: TPayload) => void;
  onConnected?: () => void;
  onDisconnected?: () => void;
}) {
  const {
    url,
    channel,
    params = {},
    protocols,
    consumerKey: consumerKeyOption,
    enabled = true,
    onReceived,
    onConnected,
    onDisconnected,
  } = options;

  const receivedRef = useRef(onReceived);
  receivedRef.current = onReceived;
  const connectedRef = useRef(onConnected);
  connectedRef.current = onConnected;
  const disconnectedRef = useRef(onDisconnected);
  disconnectedRef.current = onDisconnected;

  const paramsKey = JSON.stringify(params);
  const protocolsKey = protocols?.join(",") ?? "";

  useEffect(() => {
    if (!enabled) return;

    const key = consumerKeyOption ?? `${channel}:${paramsKey}|${protocolsKey}`;
    const consumer = getConsumer(key, url, protocols);
    const subKey = subscriptionKey(channel, params);

    const handlers: Handlers = {
      received: (data: TPayload) => receivedRef.current(data),
      connected: () => connectedRef.current?.(),
      disconnected: () => disconnectedRef.current?.(),
    };

    let instances = handlerCache.get(subKey);
    if (!instances) {
      const set = new Set<Handlers>();
      instances = set;
      handlerCache.set(subKey, set);
      subscriptionCache.set(
        subKey,
        consumer.subscriptions.create(
          { channel, ...params },
          {
            connected: () => set.forEach((h) => h.connected()),
            disconnected: () => set.forEach((h) => h.disconnected()),
            received: (data: TPayload) => set.forEach((h) => h.received(data)),
          }
        )
      );
    }
    instances.add(handlers);

    return () => {
      const set = handlerCache.get(subKey);
      if (!set) return;
      set.delete(handlers);

      if (set.size === 0) {
        subscriptionCache.get(subKey)?.unsubscribe();
        subscriptionCache.delete(subKey);
        handlerCache.delete(subKey);
      }
    };
    // url is intentionally excluded: it is a stable function read on each (re)connect.
  }, [enabled, channel, paramsKey, protocolsKey, consumerKeyOption]);
}
